import Keyboard, { KeyboardReactInterface } from 'react-simple-keyboard';
import './keyboard.styles.css';
import { ChangeEvent, ChangeEventHandler, useLayoutEffect, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowTurnDownLeft, faChevronLeft, faChevronRight, faDeleteLeft } from '@fortawesome/pro-light-svg-icons';
import { faUp } from '@fortawesome/pro-solid-svg-icons';

type KeyboardLayout = 'numeric' | 'full' | 'fullShift';

export type VirtualKeyboardRef = Pick<KeyboardReactInterface, 'setInput' | 'input' | 'getCaretPosition' | 'getInput'>;

type VirtualKeyboardProps = {
  defaultLayout?: KeyboardLayout;
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onClose: () => void;
  value: string;
  onLayoutChange?: () => void;
  syncCaret?: boolean;
  maxLength?: number;
};

export const VirtualKeyboard = ({
  defaultLayout = 'numeric',
  onChange,
  onClose,
  value,
  onLayoutChange,
  syncCaret = false,
  maxLength,
}: VirtualKeyboardProps) => {
  const [keyboardRef, setKeyboardRef] = useState<VirtualKeyboardRef | null>(null);
  const [layout, setLayout] = useState<KeyboardLayout>(defaultLayout);

  const input = keyboardRef?.input;

  const onKeyPress = (key: string) => {
    if (key === '{abc}') {
      setLayout('full');
      onLayoutChange?.();
    }

    if (key === '{123}') {
      setLayout('numeric');
      onLayoutChange?.();
    }

    if (key === '{enter}' || key === '{enter2}') {
      onClose();
    }

    if (key === '{shift}') {
      setLayout(layout === 'full' ? 'fullShift' : 'full');
    }

    if (key === '{arrowleft}') {
      const activeInput = document.activeElement as HTMLInputElement | undefined;
      const activeCaret = activeInput?.selectionStart;
      if (activeCaret && activeCaret > 0) {
        activeInput?.setSelectionRange(activeCaret - 1, activeCaret - 1);
      }
    }

    if (key === '{arrowright}') {
      const activeInput = document.activeElement as HTMLInputElement | undefined;
      const activeCaret = activeInput?.selectionStart;
      const inputLength = activeInput?.value.length || 0;
      if (typeof activeCaret === 'number' && activeCaret < inputLength) {
        activeInput?.setSelectionRange(activeCaret + 1, activeCaret + 1);
      }
    }
  };

  const handleOnChange = (input: string) => {
    const event = {
      target: {
        value: input,
      },
    } as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    onChange?.(event);
  };

  // sync carret position
  useLayoutEffect(() => {
    if (!syncCaret) {
      return;
    }

    const caretPosition = keyboardRef?.getCaretPosition();
    const activeElement = document.activeElement as HTMLInputElement | undefined;

    if (typeof caretPosition === 'number') {
      activeElement?.setSelectionRange(caretPosition, caretPosition);
    }
  }, [keyboardRef, input, value, syncCaret]);

  useLayoutEffect(() => {
    keyboardRef?.setInput(value);
  }, [value, keyboardRef]);

  return (
    <Keyboard
      keyboardRef={setKeyboardRef}
      layoutName={layout}
      maxLength={maxLength}
      preventMouseDownDefault={true}
      buttonTheme={[
        { buttons: '{abc} {123} {bksp} {arrowleft} {arrowright} {ctrl}', class: 'hg-dark-btn' },
        { buttons: ', . ? - {shift}', class: 'hg-light-btn' },
        { buttons: '{enter}', class: 'hg-enter-btn hg-active-enter-btn' },
        { buttons: '{bksp}', class: 'hg-bksp-btn' },
        { buttons: '{space}', class: 'hg-space-btn' },
        { buttons: 'A a', class: 'hg-a-btn' },
        { buttons: '{123}', class: 'hg-123-btn' },
        { buttons: '{enter2}', class: 'hg-enter2-btn hg-active-enter-btn' },
      ]}
      display={{
        '{enter}': renderToString(<FontAwesomeIcon className="h-6 w-6 text-icon-invert" icon={faArrowTurnDownLeft} />),
        '{bksp}': renderToString(<FontAwesomeIcon className="h-6 w-6 text-icon-dark" icon={faDeleteLeft} />),
        '{abc}': 'ABC',
        '{123}': '123',
        '{space}': ' ',
        '{enter2}': ' ',
        '{shift}': renderToString(<FontAwesomeIcon className="h-5 w-5 text-icon-dark" icon={faUp} />),
        '{arrowleft}': renderToString(<FontAwesomeIcon className="h-3 w-3 text-icon-dark" icon={faChevronLeft} />),
        '{arrowright}': renderToString(<FontAwesomeIcon className="h-3 w-3 text-icon-dark" icon={faChevronRight} />),
      }}
      layout={{
        numeric: ['7 4 1 {abc}', '8 5 2 0', '9 6 3 ,', '{bksp} {enter}'],
        full: [
          'q w e r t y u i o p {bksp}',
          'a s d f g h j k l - {enter}',
          '{shift} z x c v b n m , . ? {enter2}',
          '{123} {space} {arrowleft} {arrowright}',
        ],
        fullShift: [
          'Q W E R T Y U I O P {bksp}',
          'A S D F G H J K L - {enter}',
          '{shift} Z X C V B N M , . ? {enter2}',
          '{123} {space} {arrowleft} {arrowright}',
        ],
      }}
      onChange={handleOnChange}
      onKeyPress={onKeyPress}
    />
  );
};
